/**
 * 牌型判断引擎
 * 提供扑克牌牌型识别、牌力评估、牌型比较等核心算法
 * 支持5张牌（中道/尾道）和3张牌（头道）的牌型判断
 *
 * 这是前端单机比牌、AI 评分、摆牌合法性校验共同依赖的规则源。
 * 修改这里时需要同步检查后端 HandEvaluator.java，确保在线和单机规则一致。
 */

import { 
  Card, 
  HAND_RANKS, 
  sortByRank, 
  getRankCounts, 
  getSuitCounts,
  SUITS,
  RANKS,
  isJokerCard,
  normalizeCardsForEval
} from './poker';

// 判断五同（五条）
// 五同要求有5张相同点数的牌，需使用两副牌时才可能出现
export function isFiveOfAKind(cards) {
  if (cards.length !== 5) return false;

  // 统计各点数出现的次数
  const rankCounts = getRankCounts(cards);
  // 查找是否有某个点数出现了5次
  const five = Object.entries(rankCounts).find(([rank, count]) => count === 5);

  if (five) {
    const fiveRank = parseInt(five[0]);
    return {
      isFiveOfAKind: true,
      fiveRank: fiveRank === 1 ? 14 : fiveRank // A作为14点
    };
  }

  return { isFiveOfAKind: false };
}

// 判断是否是顺子（5张牌）
export function isStraight(cards) {
  // 顺子必须是5张牌，否则直接返回false
  if (cards.length !== 5) return false;

  // 获取牌的点数，A作为14点（用于常规顺子判断）
  const values = cards.map(c => Number(c.rank.value) === 1 ? 14 : Number(c.rank.value));
  values.sort((a, b) => a - b); // 升序排列，便于检查连续性

  // 特殊情况：A-2-3-4-5（最小顺子，A作为1点）
  // 先获取原始点数（A=1）并排序，检查是否构成"轮子"
  const originalValues = cards.map(c => Number(c.rank.value)).sort((a, b) => a - b);
  if (originalValues.join(',') === '1,2,3,4,5') {
    return { isStraight: true, highCard: 5, isWheel: true };
  }

  // 检查是否连续：相邻两张牌的点数差必须为1
  for (let i = 1; i < values.length; i++) {
    if (Number(values[i]) !== Number(values[i - 1]) + 1) {
      return { isStraight: false };
    }
  }

  // 是顺子，判断是否是皇家顺子（10-J-Q-K-A，即最大顺子）
  const isBroadway = Number(values[4]) === 14 && Number(values[3]) === 13;

  return {
    isStraight: true,
    highCard: Number(values[4]),
    isBroadway: isBroadway
  };
}

// 判断是否是同花（5张牌）
// 同花要求5张牌的花色完全相同
export function isFlush(cards) {
  // 同花必须是5张牌，否则直接返回false
  if (cards.length !== 5) return false;

  // 统计各花色的数量
  const suitCounts = getSuitCounts(cards);
  // 查找是否有某个花色出现了5次（即5张牌同花色）
  const flushSuit = Object.entries(suitCounts).find(([suit, count]) => count === 5);

  if (flushSuit) {
    // 是同花，将牌按点数从大到小排序后返回
    const sorted = sortByRank(cards);
    return {
      isFlush: true,
      suit: flushSuit[0],
      cards: sorted
    };
  }

  return { isFlush: false };
}

// 判断是否是同花顺
// 同花顺需同时满足同花和顺子两个条件
export function isStraightFlush(cards) {
  const flushResult = isFlush(cards);
  if (!flushResult.isFlush) return { isStraightFlush: false };
  
  const straightResult = isStraight(cards);
  if (!straightResult.isStraight) return { isStraightFlush: false };
  
  return {
    isStraightFlush: true,
    highCard: straightResult.highCard,
    isRoyal: straightResult.highCard === 14 && straightResult.isBroadway,
    isWheel: straightResult.isWheel
  };
}

// 判断铁支（四条）
// 铁支要求有4张相同点数的牌加1张踢脚牌
export function isFourOfAKind(cards) {
  if (cards.length !== 5) return false;

  // 统计各点数出现的次数
  const rankCounts = getRankCounts(cards);
  // 查找是否有某个点数出现了4次
  const four = Object.entries(rankCounts).find(([rank, count]) => count === 4);

  if (four) {
    const fourRank = parseInt(four[0]);
    // 找出那张不属于四条的踢脚牌
    const kicker = cards.find(c => c.rank.value !== fourRank);
    return {
      isFourOfAKind: true,
      fourRank: fourRank === 1 ? 14 : fourRank, // A作为14点
      kicker: Number(kicker.rank.value) === 1 ? 14 : Number(kicker.rank.value) // 踢脚牌A也作为14点
    };
  }

  return { isFourOfAKind: false };
}

// 判断葫芦（满堂红）
// 葫芦要求有3张相同点数的牌加另外2张相同点数的牌
export function isFullHouse(cards) {
  if (cards.length !== 5) return false;

  // 统计各点数出现的次数
  const rankCounts = getRankCounts(cards);
  const entries = Object.entries(rankCounts);

  // 查找是否有三条和对子
  const three = entries.find(([rank, count]) => count === 3);
  const pair = entries.find(([rank, count]) => count === 2);

  if (three && pair) {
    return {
      isFullHouse: true,
      threeRank: parseInt(three[0]) === 1 ? 14 : parseInt(three[0]), // 三条的点数，A作为14点
      pairRank: parseInt(pair[0]) === 1 ? 14 : parseInt(pair[0]) // 对子的点数，A作为14点
    };
  }

  return { isFullHouse: false };
}

// 判断三条
// 三条要求有3张相同点数的牌
// 支持5张牌（中道/尾道）和3张牌（头道）两种情况
export function isThreeOfAKind(cards) {
  if (cards.length !== 5 && cards.length !== 3) return false;

  // 统计各点数出现的次数
  const rankCounts = getRankCounts(cards);
  // 查找是否有某个点数出现了3次
  const three = Object.entries(rankCounts).find(([rank, count]) => count === 3);

  // 5张牌中的三条：需要额外返回踢脚牌信息
  if (three && cards.length === 5) {
    const threeRank = parseInt(three[0]);
    // 过滤出非三条的牌作为踢脚牌，并按点数从大到小排序
    const kickers = cards
      .filter(c => c.rank.value !== threeRank)
      .sort((a, b) => {
        const valA = Number(a.rank.value) === 1 ? 14 : Number(a.rank.value);
        const valB = Number(b.rank.value) === 1 ? 14 : Number(b.rank.value);
        return valB - valA;
      });

    return {
      isThreeOfAKind: true,
      threeRank: threeRank === 1 ? 14 : threeRank, // 三条的点数，A作为14点
      kickers: kickers.map(c => Number(c.rank.value) === 1 ? 14 : Number(c.rank.value)) // 踢脚牌点数列表
    };
  }

  // 3张牌中的三条：头道三条，无需踢脚牌
  if (three && cards.length === 3) {
    return {
      isThreeOfAKind: true,
      threeRank: parseInt(three[0]) === 1 ? 14 : parseInt(three[0]) // A作为14点
    };
  }

  return { isThreeOfAKind: false };
}

// 判断两对
// 两对要求有两组各2张相同点数的牌，加1张踢脚牌
export function isTwoPairs(cards) {
  if (cards.length !== 5) return false;

  // 统计各点数出现的次数
  const rankCounts = getRankCounts(cards);
  // 过滤出所有出现2次的点数（即对子）
  const pairs = Object.entries(rankCounts).filter(([rank, count]) => count === 2);

  // 恰好有两个对子
  if (pairs.length === 2) {
    // 提取两个对子的点数，并按从大到小排序（A作为14点）
    const pairRanks = pairs.map(p => parseInt(p[0])).sort((a, b) => {
      const valA = a === 1 ? 14 : a;
      const valB = b === 1 ? 14 : b;
      return valB - valA;
    });

    // 找出不属于任何对子的那张踢脚牌
    const kicker = cards.find(c =>
      c.rank.value !== pairRanks[0] && c.rank.value !== pairRanks[1]
    );

    return {
      isTwoPairs: true,
      highPair: pairRanks[0] === 1 ? 14 : pairRanks[0], // 较大对子的点数
      lowPair: pairRanks[1] === 1 ? 14 : pairRanks[1], // 较小对子的点数
      kicker: kicker.rank.value === 1 ? 14 : kicker.rank.value // 踢脚牌点数
    };
  }

  return { isTwoPairs: false };
}

// 判断一对
// 一对要求有2张相同点数的牌
// 支持5张牌（中道/尾道）和3张牌（头道）两种情况
export function isOnePair(cards) {
  if (cards.length !== 5 && cards.length !== 3) return false;

  // 统计各点数出现的次数
  const rankCounts = getRankCounts(cards);
  // 查找是否有某个点数出现了2次
  const pair = Object.entries(rankCounts).find(([rank, count]) => count === 2);

  if (pair) {
    const pairRank = parseInt(pair[0]);
    // 过滤出非对子的牌作为踢脚牌，并按点数从大到小排序
    const kickers = cards
      .filter(c => c.rank.value !== pairRank)
      .sort((a, b) => {
        const valA = Number(a.rank.value) === 1 ? 14 : Number(a.rank.value);
        const valB = Number(b.rank.value) === 1 ? 14 : Number(b.rank.value);
        return valB - valA;
      });

    return {
      isOnePair: true,
      pairRank: pairRank === 1 ? 14 : pairRank, // 对子的点数，A作为14点
      kickers: kickers.map(c => Number(c.rank.value) === 1 ? 14 : Number(c.rank.value)) // 踢脚牌点数列表
    };
  }

  return { isOnePair: false };
}

// 获取5张牌的完整牌型信息
// 按照牌型从大到小的顺序依次检测，返回包含牌型等级和详细信息的评估结果
/**
 * 为鬼牌生成所有可能的替代牌（52张标准牌）
 * @returns {Array<{suit: object, rank: object}>}
 */
function getAllSubstitutionCards() {
  const cards = []
  const suits = Object.values(SUITS).filter(s => s.name !== 'joker')
  const ranks = Object.values(RANKS).filter(r => r.name !== 'Joker')
  for (const suit of suits) {
    for (const rank of ranks) {
      cards.push({ suit, rank })
    }
  }
  return cards
}

const _allSubCards = getAllSubstitutionCards()
const _rankByValue = Object.values(RANKS)
  .filter(r => r.name !== 'Joker')
  .reduce((acc, r) => {
    acc[r.value] = r
    return acc
  }, {})
const _rankValues1To13 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]
const _nonJokerSuits = Object.values(SUITS).filter(s => s.name !== 'joker')

function maxNaturalSuitCount(naturalCards) {
  const counts = getSuitCounts(naturalCards)
  const values = Object.values(counts)
  return values.length ? Math.max(...values) : 0
}

/** 同花/同花顺才依赖鬼替的具体花色；其余牌型做点数枚举即可 */
function needsSuitAwareGhostResolve(naturalCards, ghostCount) {
  const max = maxNaturalSuitCount(naturalCards)
  if (max >= 4) return true
  if (max >= 3 && ghostCount >= 1) return true
  return false
}

function makeSyntheticGhostCard(rankValue, slot) {
  const rank = _rankByValue[rankValue]
  const suit = _nonJokerSuits[slot % _nonJokerSuits.length]
  return new Card(suit, rank, undefined, 800 + slot)
}

function considerGhostResolved(naturalCards, resolved, state) {
  const result = evaluateFiveCardsRaw(resolved)
  if (!state.bestResult || compareHandResults(result, state.bestResult) > 0) {
    state.bestResult = result
    state.bestCards = resolved
  }
}

function tryRankAssignmentGhosts(naturalCards, ghostCount, state) {
  if (ghostCount === 1) {
    for (const rv of _rankValues1To13) {
      considerGhostResolved(naturalCards, [...naturalCards, makeSyntheticGhostCard(rv, 0)], state)
    }
    return
  }
  for (const r1 of _rankValues1To13) {
    for (const r2 of _rankValues1To13) {
      considerGhostResolved(naturalCards, [
        ...naturalCards,
        makeSyntheticGhostCard(r1, 0),
        makeSyntheticGhostCard(r2, 1)
      ], state)
    }
  }
}

function collectPhysicalGhostSubs(alreadyUsedIds, suitName) {
  const list = []
  for (const sub of _allSubCards) {
    if (suitName && sub.suit.name !== suitName) continue
    for (const copyIndex of [0, 701, 702]) {
      const card = new Card(sub.suit, sub.rank, undefined, copyIndex)
      if (!alreadyUsedIds.has(card.id)) list.push(card)
    }
  }
  return list
}

function trySuitAwareGhostReplacements(naturalCards, ghostCount, alreadyUsedIds, state) {
  const dominantSuits = []
  const suitCounts = getSuitCounts(naturalCards)
  for (const [name, count] of Object.entries(suitCounts)) {
    if (count >= 3) dominantSuits.push(name)
  }
  if (dominantSuits.length === 0) {
    dominantSuits.push(..._nonJokerSuits.map(s => s.name))
  }

  const subs = []
  for (const suitName of dominantSuits) {
    subs.push(...collectPhysicalGhostSubs(alreadyUsedIds, suitName))
  }

  if (ghostCount === 1) {
    for (const sub of subs) {
      considerGhostResolved(naturalCards, [...naturalCards, sub], state)
    }
    return
  }

  for (let i = 0; i < subs.length; i++) {
    for (let j = i; j < subs.length; j++) {
      if (i === j && subs[i].copyIndex === subs[j].copyIndex) continue
      considerGhostResolved(naturalCards, [...naturalCards, subs[i], subs[j]], state)
    }
  }
}

/**
 * 替换鬼牌生成实际手牌（优先点数枚举，必要时再做同花向的实体替牌）。
 */
function resolveGhostCards(cards) {
  const normalized = normalizeCardsForEval(cards)
  const naturalCards = normalized.filter(c => !isJokerCard(c))
  const ghostCount = normalized.length - naturalCards.length

  if (ghostCount === 0) {
    return { cards: normalized, usedGhosts: 0 }
  }

  const alreadyUsedIds = new Set(naturalCards.map(c => c.id))
  const state = { bestResult: null, bestCards: null }

  tryRankAssignmentGhosts(naturalCards, ghostCount, state)
  if (needsSuitAwareGhostResolve(naturalCards, ghostCount)) {
    trySuitAwareGhostReplacements(naturalCards, ghostCount, alreadyUsedIds, state)
  }

  return {
    cards: state.bestCards || normalized,
    usedGhosts: ghostCount
  }
}

/**
 * 比较两个牌型结果的大小
 * @returns {number} 正数=result1>result2, 0=相等, 负数=result1<result2
 */
function compareHandResults(r1, r2) {
  if (r1.rank.value !== r2.rank.value) {
    return r1.rank.value - r2.rank.value
  }
  if (r1.rank.value === 10) {
    return (r1.fiveRank || 0) - (r2.fiveRank || 0)
  }
  // Same rank, compare key properties
  if (r1.rank.value === 9 || r1.rank.value === 5) {
    return (r1.highCard || 0) - (r2.highCard || 0)
  }
  if (r1.rank.value === 8) {
    if (r1.fourRank !== r2.fourRank) return (r1.fourRank || 0) - (r2.fourRank || 0)
    return (r1.kicker || 0) - (r2.kicker || 0)
  }
  if (r1.rank.value === 7) {
    if (r1.threeRank !== r2.threeRank) return (r1.threeRank || 0) - (r2.threeRank || 0)
    return (r1.pairRank || 0) - (r2.pairRank || 0)
  }
  if (r1.rank.value === 4) {
    if (r1.threeRank !== r2.threeRank) return (r1.threeRank || 0) - (r2.threeRank || 0)
    return 0
  }
  if (r1.rank.value === 2) {
    if (r1.pairRank !== r2.pairRank) return (r1.pairRank || 0) - (r2.pairRank || 0)
    const k1 = r1.kickers || []
    const k2 = r2.kickers || []
    for (let i = 0; i < Math.max(k1.length, k2.length); i++) {
      if ((k1[i] || 0) !== (k2[i] || 0)) return (k1[i] || 0) - (k2[i] || 0)
    }
    return 0
  }
  if (r1.rank.value === 1) {
    const v1 = r1.values || (r1.cards || []).map(c => Number(c.rank.value) === 1 ? 14 : Number(c.rank.value))
    const v2 = r2.values || (r2.cards || []).map(c => Number(c.rank.value) === 1 ? 14 : Number(c.rank.value))
    for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
      if ((v1[i] || 0) !== (v2[i] || 0)) return (v1[i] || 0) - (v2[i] || 0)
    }
    return 0
  }
  return 0
}

/**
 * 原始5张牌评估（内部使用，不处理鬼牌）
 */
function evaluateFiveCardsRaw(cards) {
  const foak5 = isFiveOfAKind(cards);
  if (foak5.isFiveOfAKind) {
    return { rank: HAND_RANKS.FIVE_OF_A_KIND, fiveRank: foak5.fiveRank, cards: sortByRank(cards) };
  }
  const sf = isStraightFlush(cards);
  if (sf.isStraightFlush) {
    return { rank: HAND_RANKS.ROYAL_FLUSH, highCard: sf.highCard, isRoyal: sf.isRoyal, isWheel: sf.isWheel, cards: sortByRank(cards) };
  }
  const foak = isFourOfAKind(cards);
  if (foak.isFourOfAKind) {
    return { rank: HAND_RANKS.FOUR_OF_A_KIND, fourRank: foak.fourRank, kicker: foak.kicker, cards: sortByRank(cards) };
  }
  const fh = isFullHouse(cards);
  if (fh.isFullHouse) {
    return { rank: HAND_RANKS.FULL_HOUSE, threeRank: fh.threeRank, pairRank: fh.pairRank, cards: sortByRank(cards) };
  }
  const fl = isFlush(cards);
  if (fl.isFlush) {
    return { rank: HAND_RANKS.FLUSH, suit: fl.suit, cards: fl.cards };
  }
  const st = isStraight(cards);
  if (st.isStraight) {
    return { rank: HAND_RANKS.STRAIGHT, highCard: st.highCard, isWheel: st.isWheel, cards: sortByRank(cards) };
  }
  const toak = isThreeOfAKind(cards);
  if (toak.isThreeOfAKind) {
    return { rank: HAND_RANKS.THREE_OF_A_KIND, threeRank: toak.threeRank, kickers: toak.kickers, cards: sortByRank(cards) };
  }
  const tp = isTwoPairs(cards);
  if (tp.isTwoPairs) {
    return { rank: HAND_RANKS.TWO_PAIRS, highPair: tp.highPair, lowPair: tp.lowPair, kicker: tp.kicker, cards: sortByRank(cards) };
  }
  const op = isOnePair(cards);
  if (op.isOnePair) {
    return { rank: HAND_RANKS.ONE_PAIR, pairRank: op.pairRank, kickers: op.kickers, cards: sortByRank(cards) };
  }
  const sorted = sortByRank(cards);
  return { rank: HAND_RANKS.HIGH_CARD, cards: sorted, values: sorted.map(c => Number(c.rank.value) === 1 ? 14 : Number(c.rank.value)) };
}

export function evaluateFiveCards(cards) {
  const normalized = normalizeCardsForEval(cards)
  if (normalized.length !== 5) {
    throw new Error('必须提供5张牌');
  }

  const hasGhost = normalized.some(c => isJokerCard(c))
  if (hasGhost) {
    const resolved = resolveGhostCards(normalized)
    const result = evaluateFiveCardsRaw(resolved.cards)
    result.usedGhosts = resolved.usedGhosts
    result.originalCards = normalized
    return result
  }

  const result = evaluateFiveCardsRaw(normalized)
  result.usedGhosts = 0
  return result
}

// 获取3张牌（头道）的牌型信息
// 头道只有3张牌，可能的牌型只有：三条、一对、散牌
export function evaluateThreeCards(cards) {
  const normalized = normalizeCardsForEval(cards)
  if (normalized.length !== 3) {
    throw new Error('必须提供3张牌');
  }

  const hasGhost = normalized.some(c => isJokerCard(c))
  let resolvedCards = normalized
  let usedGhosts = 0
  if (hasGhost) {
    const naturalCards = normalized.filter(c => !isJokerCard(c))
    usedGhosts = normalized.length - naturalCards.length

    let best = null
    let bestResult = null

    const consider3 = (resolved) => {
      let candidate = null
      const toak = isThreeOfAKind(resolved)
      if (toak.isThreeOfAKind) {
        candidate = { rank: HAND_RANKS.THREE_OF_A_KIND, threeRank: toak.threeRank }
      } else {
        const op = isOnePair(resolved)
        if (op.isOnePair) {
          candidate = { rank: HAND_RANKS.ONE_PAIR, pairRank: op.pairRank, kickers: op.kickers }
        } else {
          const sorted = sortByRank(resolved)
          candidate = {
            rank: HAND_RANKS.HIGH_CARD,
            cards: sorted,
            values: sorted.map(c => Number(c.rank.value) === 1 ? 14 : Number(c.rank.value))
          }
        }
      }
      if (!bestResult || compareHandResults(candidate, bestResult) > 0) {
        bestResult = candidate
        best = resolved
      }
    }

    if (usedGhosts === 1) {
      for (const rv of _rankValues1To13) {
        consider3([...naturalCards, makeSyntheticGhostCard(rv, 0)])
      }
    } else {
      for (const r1 of _rankValues1To13) {
        for (const r2 of _rankValues1To13) {
          consider3([
            ...naturalCards,
            makeSyntheticGhostCard(r1, 0),
            makeSyntheticGhostCard(r2, 1)
          ])
        }
      }
    }
    if (best) resolvedCards = best
  }

  const toak = isThreeOfAKind(resolvedCards)
  if (toak.isThreeOfAKind) {
    return {
      rank: HAND_RANKS.THREE_OF_A_KIND,
      threeRank: toak.threeRank,
      cards: sortByRank(resolvedCards),
      usedGhosts,
      originalCards: hasGhost ? normalized : undefined
    }
  }

  const op = isOnePair(resolvedCards)
  if (op.isOnePair) {
    return {
      rank: HAND_RANKS.ONE_PAIR,
      pairRank: op.pairRank,
      kickers: op.kickers,
      cards: sortByRank(resolvedCards),
      usedGhosts,
      originalCards: hasGhost ? normalized : undefined
    }
  }

  const sorted = sortByRank(resolvedCards)
  return {
    rank: HAND_RANKS.HIGH_CARD,
    cards: sorted,
    values: sorted.map(c => Number(c.rank.value) === 1 ? 14 : Number(c.rank.value)),
    usedGhosts,
    originalCards: hasGhost ? normalized : undefined
  }
}

function getStraightCompareValue(evalResult) {
  if (evalResult.isWheel) return 13.5;
  return evalResult.highCard;
}

function compareGhostTiebreaker(usedGhosts1, usedGhosts2) {
  const g1 = usedGhosts1 || 0
  const g2 = usedGhosts2 || 0
  if (g1 !== g2) {
    return g1 < g2 ? 1 : -1
  }
  return 0
}

// 比较两个5张牌牌型的大小
// 返回值：1表示hand1大，-1表示hand2大，0表示相等
export function compareFiveCards(hand1, hand2) {
  // 如果传入的是原始牌数组，先进行牌型评估
  const eval1 = typeof hand1.rank === 'object' ? hand1 : evaluateFiveCards(hand1);
  const eval2 = typeof hand2.rank === 'object' ? hand2 : evaluateFiveCards(hand2);

  // 第一步：先比较牌型等级（如铁支 > 葫芦 > 同花等）
  if (eval1.rank.value !== eval2.rank.value) {
    return eval1.rank.value > eval2.rank.value ? 1 : -1;
  }

  // 第二步：同等级牌型，比较拼凑后的最大牌力（含鬼牌替代结果）
  let strength = 0
  switch (eval1.rank.value) {
    case 10: // 五同：比较五条点数
      strength = eval1.fiveRank > eval2.fiveRank ? 1 :
             eval1.fiveRank < eval2.fiveRank ? -1 : 0;
      break;

    case 9: // 同花顺：比较最大牌的点数
      {
        const straightValue1 = getStraightCompareValue(eval1);
        const straightValue2 = getStraightCompareValue(eval2);
        strength = straightValue1 > straightValue2 ? 1 :
               straightValue1 < straightValue2 ? -1 : 0;
      }
      break;

    case 8: // 炸弹：先比较四条点数，再比较踢脚牌
      if (eval1.fourRank !== eval2.fourRank) {
        strength = eval1.fourRank > eval2.fourRank ? 1 : -1;
      } else {
        strength = eval1.kicker > eval2.kicker ? 1 :
               eval1.kicker < eval2.kicker ? -1 : 0;
      }
      break;

    case 7: // 葫芦：先比较三条点数，再比较对子点数
      if (eval1.threeRank !== eval2.threeRank) {
        strength = eval1.threeRank > eval2.threeRank ? 1 : -1;
      } else {
        strength = eval1.pairRank > eval2.pairRank ? 1 :
               eval1.pairRank < eval2.pairRank ? -1 : 0;
      }
      break;

    case 6: // 同花：逐张从大到小比较点数
    case 1: // 乌龙：逐张从大到小比较点数
      for (let i = 0; i < 5; i++) {
        const val1 = Number(eval1.cards[i].rank.value) === 1 ? 14 : Number(eval1.cards[i].rank.value);
        const val2 = Number(eval2.cards[i].rank.value) === 1 ? 14 : Number(eval2.cards[i].rank.value);
        if (val1 !== val2) {
          strength = val1 > val2 ? 1 : -1;
          break
        }
      }
      break;

    case 5: // 顺子：比较最大牌的点数
      {
        const straightValue1 = getStraightCompareValue(eval1);
        const straightValue2 = getStraightCompareValue(eval2);
        strength = straightValue1 > straightValue2 ? 1 :
               straightValue1 < straightValue2 ? -1 : 0;
      }
      break;

    case 4: // 三条：先比较三条点数，再逐张比较踢脚牌
      if (eval1.threeRank !== eval2.threeRank) {
        strength = eval1.threeRank > eval2.threeRank ? 1 : -1;
      } else {
        for (let i = 0; i < eval1.kickers.length; i++) {
          if (eval1.kickers[i] !== eval2.kickers[i]) {
            strength = eval1.kickers[i] > eval2.kickers[i] ? 1 : -1;
            break
          }
        }
      }
      break;

    case 3: // 两对：先比较大对子，再比较小对子，最后比较踢脚牌
      if (eval1.highPair !== eval2.highPair) {
        strength = eval1.highPair > eval2.highPair ? 1 : -1;
      } else if (eval1.lowPair !== eval2.lowPair) {
        strength = eval1.lowPair > eval2.lowPair ? 1 : -1;
      } else {
        strength = eval1.kicker > eval2.kicker ? 1 :
               eval1.kicker < eval2.kicker ? -1 : 0;
      }
      break;

    case 2: // 一对：先比较对子点数，再逐张比较踢脚牌
      if (eval1.pairRank !== eval2.pairRank) {
        strength = eval1.pairRank > eval2.pairRank ? 1 : -1;
      } else {
        for (let i = 0; i < eval1.kickers.length; i++) {
          if (eval1.kickers[i] !== eval2.kickers[i]) {
            strength = eval1.kickers[i] > eval2.kickers[i] ? 1 : -1;
            break
          }
        }
      }
      break;

    default:
      strength = 0;
  }

  if (strength !== 0) {
    return strength
  }

  // 第三步：牌力完全相同且同牌型时，天然无鬼 > 带鬼；双方都带鬼则视为平
  return compareGhostTiebreaker(eval1.usedGhosts, eval2.usedGhosts)
}

// 比较两个3张牌牌型的大小
// 返回值：1表示hand1大，-1表示hand2大，0表示相等
export function compareThreeCards(hand1, hand2) {
  // 如果传入的是原始牌数组，先进行牌型评估
  const eval1 = typeof hand1.rank === 'object' ? hand1 : evaluateThreeCards(hand1);
  const eval2 = typeof hand2.rank === 'object' ? hand2 : evaluateThreeCards(hand2);

  // 第一步：先比较牌型等级（三条 > 一对 > 散牌）
  if (eval1.rank.value !== eval2.rank.value) {
    return eval1.rank.value > eval2.rank.value ? 1 : -1;
  }

  // 第二步：同等级牌型，比较拼凑后的最大牌力
  let strength = 0
  switch (eval1.rank.value) {
    case 4: // 三条：比较三条的点数大小
      strength = eval1.threeRank > eval2.threeRank ? 1 :
             eval1.threeRank < eval2.threeRank ? -1 : 0;
      break;

    case 2: // 一对：先比较对子点数，再比较踢脚牌
      if (eval1.pairRank !== eval2.pairRank) {
        strength = eval1.pairRank > eval2.pairRank ? 1 : -1;
      } else {
        strength = eval1.kickers[0] > eval2.kickers[0] ? 1 :
               eval1.kickers[0] < eval2.kickers[0] ? -1 : 0;
      }
      break;

    case 1: // 乌龙：逐张从大到小比较点数
      for (let i = 0; i < 3; i++) {
        const val1 = Number(eval1.cards[i].rank.value) === 1 ? 14 : Number(eval1.cards[i].rank.value);
        const val2 = Number(eval2.cards[i].rank.value) === 1 ? 14 : Number(eval2.cards[i].rank.value);
        if (val1 !== val2) {
          strength = val1 > val2 ? 1 : -1;
          break
        }
      }
      break;

    default:
      strength = 0;
  }

  if (strength !== 0) {
    return strength
  }

  // 第三步：牌力相同时，天然无鬼 > 带鬼
  return compareGhostTiebreaker(eval1.usedGhosts, eval2.usedGhosts)
}

// 检查摆牌是否合法（尾 >= 中 >= 头）
// 十三水规则要求：尾道（5张）牌力 >= 中道（5张）牌力 >= 头道（3张）牌力
export function isValidLayout(front, middle, back) {
  // 第一步：检查各道的牌数是否正确
  if (front.length !== 3 || middle.length !== 5 || back.length !== 5) {
    return { valid: false, reason: '牌数不正确' };
  }

  // 第二步：评估各道的牌型
  const frontEval = evaluateThreeCards(front);   // 头道3张牌评估
  const middleEval = evaluateFiveCards(middle);  // 中道5张牌评估
  const backEval = evaluateFiveCards(back);      // 尾道5张牌评估

  // 第三步：比较中道和尾道（中道牌力不能大于尾道）
  const middleVsBack = compareFiveCards(middleEval, backEval);
  if (middleVsBack > 0) {
    return { valid: false, reason: '中道大于尾道' };
  }

  // 第四步：比较头道和中道（头道牌力不能大于中道）
  // 由于头道是3张牌评估体系，中道是5张牌评估体系，需要进行rank映射
  // 正确的映射：头道rank.value -> 5张牌rank.value
  const frontRankMap = {
    4: 4,  // 头道三条 -> 5张牌三条(value=4)
    2: 2,  // 头道一对 -> 5张牌一对(value=2)
    1: 1   // 头道乌龙 -> 5张牌乌龙(value=1)
  };

  const frontRank = frontRankMap[frontEval.rank.value];
  const middleRank = middleEval.rank.value;

  // 如果头道映射后的牌型等级大于中道，则不合法
  if (frontRank > middleRank) {
    return { valid: false, reason: '头道大于中道' };
  }

  // 第五步：如果映射后的牌型等级相同，需要详细比较具体牌力
  if (frontRank === middleRank) {
    let frontStronger = false;

    if (frontRank === 4) {
      // 都是三条：直接比较三条的点数大小
      frontStronger = frontEval.threeRank > middleEval.threeRank;
    } else if (frontRank === 2) {
      // 都是一对：先比较对子点数
      if (frontEval.pairRank !== middleEval.pairRank) {
        frontStronger = frontEval.pairRank > middleEval.pairRank;
      } else {
        // 对子点数相同，比较最大踢脚牌
        const frontKicker = Math.max(...frontEval.kickers);
        const middleKicker = middleEval.kickers ? Math.max(...middleEval.kickers) : 0;
        frontStronger = frontKicker > middleKicker;
      }
    } else if (frontRank === 1) {
      // 都是乌龙：逐张从大到小比较点数
      // 先将头道和中道的牌按点数从大到小排序（A作为14点）
      const frontValues = frontEval.cards.map(c => Number(c.rank.value) === 1 ? 14 : Number(c.rank.value)).sort((a, b) => b - a);
      const middleValues = middleEval.cards.map(c => Number(c.rank.value) === 1 ? 14 : Number(c.rank.value)).sort((a, b) => b - a);
      // 只比较头道的3张牌（中道取最大的3张进行比较）
      for (let i = 0; i < 3; i++) {
        if (frontValues[i] > middleValues[i]) {
          frontStronger = true;
          break;
        } else if (frontValues[i] < middleValues[i]) {
          break;
        }
      }
    }

    // 如果头道牌力大于中道，则不合法
    if (frontStronger) {
      return { valid: false, reason: '头道大于中道' };
    }
  }

  // 所有检查通过，摆牌合法
  return { valid: true };
}
